import {
  FormGoal,
  FormSubGoal,
  GoalField,
} from 'components/GoalsAndSubgoalsForm/interfaces';
import {
  ScorecardGoal,
  ScorecardParam,
  ScorecardSubGoal,
} from 'store/interfaces';
import { FormValues } from 'components/ScorecardDetails/interfaces';
import {
  ALLOW_SCORECARD_OWNER_EDITS_PARAMETER_TYPE,
  CALCULATE_SELF_ASSESSMENT_PARAMETER_TYPE,
  ALLOW_SCORECARD_COMMENT_PARAMETER_TYPE,
  ENABLE_SELF_ASSESSMENT_PARAMETER_TYPE,
  ALLOW_SCORECARD_METRIC_PM_OWNER_EDITS_PARAMETER_TYPE,
  AUTO_ASSIGN_PM_REVIEWER_PARAMETER_TYPE,
  LIMIT_REVIEWERS_TO_EMPLOYEE_AND_PM_PARAMETER_TYPE,
  ALLOW_SCORECARD_OWNER_EDIT_ACTUAL_FIELD_PARAMETER_TYPE,
  SCORECARD_GOAL_TYPE_METRIC,
  MetricFormat,
  MetricPerformanceFactor,
} from 'constants/scorecards';
import { ParamTypeOption } from 'store/options/interfaces/ParamTypeOption';
import { isCuSatGoal } from './isCuSatGoal';
import getGoalEditPermissions from './getGoalEditPermissions';

export interface MetricEditContext {
  hasRestrictionForTemplateGoal: boolean;
  isRevOpsManager: boolean;
  isReviewCycleManager: boolean;
}

export enum SelfAssessmentSetups {
  request = 'request',
  calculate = 'calculate',
  disable = 'disable',
}

// Subgoals stored before the metric fields existed come back as null, and the
// metric editor renders the defaults for them — so form state has to hold those
// defaults too, or `required` rejects a select the user sees as filled in.
export function metricSubgoalDefaults(subgoal: {
  metricFormat?: string | null;
  metricPerformanceFactor?: string | null;
}) {
  return {
    metricFormat: subgoal.metricFormat || MetricFormat.NUMBER,
    metricPerformanceFactor:
      subgoal.metricPerformanceFactor || MetricPerformanceFactor.PRORATED,
  };
}

function parseSubgoals(items: ScorecardSubGoal[]): FormSubGoal[] {
  return items.map(
    ({
      assessmentScaleGroup,
      id,
      name,
      description,
      orderRank,
      weight,
      metricFormat,
      metricTarget,
      metricPerformanceFactor,
    }) => ({
      id,
      name,
      description,
      orderRank,
      weight,
      assessmentScaleGroupId: assessmentScaleGroup?.id || '',
      metricTarget,
      ...metricSubgoalDefaults({ metricFormat, metricPerformanceFactor }),
    }),
  );
}

export function parseParams(params: ScorecardParam[]) {
  const calculateSelfAssessmentParam = params?.find(
    (p) => p.type?.alias === CALCULATE_SELF_ASSESSMENT_PARAMETER_TYPE,
  );
  const allowScorecardOwnerEditsParam = params?.find(
    (p) => p.type?.alias === ALLOW_SCORECARD_OWNER_EDITS_PARAMETER_TYPE,
  );
  const allowScorecardCommentParam = params?.find(
    (p) => p.type?.alias === ALLOW_SCORECARD_COMMENT_PARAMETER_TYPE,
  );
  const enableSelfAssessmentParam = params?.find(
    (p) => p.type?.alias === ENABLE_SELF_ASSESSMENT_PARAMETER_TYPE,
  );
  const allowScorecardMetricPmOwnerEditsParam = params?.find(
    (p) =>
      p.type?.alias === ALLOW_SCORECARD_METRIC_PM_OWNER_EDITS_PARAMETER_TYPE,
  );
  const allowScorecardOwnerEditActualFieldParam = params?.find(
    (p) =>
      p.type?.alias === ALLOW_SCORECARD_OWNER_EDIT_ACTUAL_FIELD_PARAMETER_TYPE,
  );
  const autoAssignPmReviewerParam = params?.find(
    (p) => p.type?.alias === AUTO_ASSIGN_PM_REVIEWER_PARAMETER_TYPE,
  );
  const limitReviewersToEmployeeAndPmParam = params?.find(
    (p) => p.type?.alias === LIMIT_REVIEWERS_TO_EMPLOYEE_AND_PM_PARAMETER_TYPE,
  );

  let selfAssessmentSetup: SelfAssessmentSetups;

  if (calculateSelfAssessmentParam?.value) {
    selfAssessmentSetup = SelfAssessmentSetups.calculate;
  } else if (enableSelfAssessmentParam?.value) {
    selfAssessmentSetup = SelfAssessmentSetups.request;
  } else {
    selfAssessmentSetup = SelfAssessmentSetups.disable;
  }

  return {
    allowScorecardComment: allowScorecardCommentParam?.value,
    allowScorecardOwnerEdits: allowScorecardOwnerEditsParam?.value,
    calculateSelfAssessment: calculateSelfAssessmentParam?.value,
    enableSelfAssessment: enableSelfAssessmentParam?.value,
    allowScorecardMetricPmOwnerEdits:
      allowScorecardMetricPmOwnerEditsParam?.value,
    autoAssignPmReviewer: autoAssignPmReviewerParam?.value,
    limitReviewersToEmployeeAndPm: limitReviewersToEmployeeAndPmParam?.value,
    allowScorecardOwnerEditActualField:
      allowScorecardOwnerEditActualFieldParam?.value,
    selfAssessmentSetup,
  };
}

export function parseParamTypes(
  paramTypes: ParamTypeOption[],
  isTemplate: boolean,
) {
  const scorecardParams: ScorecardParam[] = paramTypes.map(
    ({ id, alias, defaultValue, templateDefaultValue }) => ({
      id,
      type: {
        id,
        alias,
      },
      value: isTemplate ? templateDefaultValue : defaultValue,
    }),
  );

  return parseParams(scorecardParams);
}

export function parseScorecardData(goals: ScorecardGoal[]): FormGoal[] {
  return goals.map(
    ({
      subgoals,
      type,
      id,
      fromGoalTemplateId,
      name,
      description,
      weight,
      orderRank,
      isCustom,
      extraMultiplier,
      params,
    }) => {
      const {
        allowScorecardComment,
        allowScorecardOwnerEdits,
        allowScorecardMetricPmOwnerEdits,
        allowScorecardOwnerEditActualField,
        selfAssessmentSetup,
        autoAssignPmReviewer,
        limitReviewersToEmployeeAndPm,
      } = parseParams(params);
      return {
        id,
        fromGoalTemplateId,
        name,
        description: description ?? undefined,
        weight,
        orderRank,
        isCustom,
        extraMultiplierId: extraMultiplier?.id || null,
        subgoals: parseSubgoals(subgoals),
        type,
        allowScorecardComment: allowScorecardComment ?? false,
        allowScorecardOwnerEdits: allowScorecardOwnerEdits ?? false,
        allowScorecardMetricPmOwnerEdits:
          allowScorecardMetricPmOwnerEdits ?? false,
        allowScorecardOwnerEditActualField:
          allowScorecardOwnerEditActualField ?? false,
        autoAssignPmReviewer: autoAssignPmReviewer ?? false,
        limitReviewersToEmployeeAndPm: limitReviewersToEmployeeAndPm ?? false,
        selfAssessmentSetup,
      };
    },
  );
}

export function formatScorecardData(
  { id: scorecardId, goals, totalWeight, entity, ...rest }: FormValues,
  version: number,
  withRestrictions: boolean = false,
  isActiveOwnScorecard: boolean = false,
  metricEditContext: MetricEditContext = {
    hasRestrictionForTemplateGoal: false,
    isRevOpsManager: false,
    isReviewCycleManager: false,
  },
): GoalField {
  return {
    ...rest,
    expectedVersion: version,
    goals: goals.map((goal, goalIndex) => {
      const {
        weight: goalWeight,
        subgoals,
        name,
        id,
        description,
        allowScorecardOwnerEdits,
        allowScorecardMetricPmOwnerEdits,
        allowScorecardOwnerEditActualField,
        allowScorecardComment,
        selfAssessmentSetup,
        autoAssignPmReviewer,
        limitReviewersToEmployeeAndPm,
        type: goalType,
      } = goal;
      const goalFields = {
        weight: Number.parseInt(goalWeight as string, 10),
        type: goalType,
      };
      if (id) {
        Object.assign(goalFields, { id });
      }

      const isMetricGoal = goalType === SCORECARD_GOAL_TYPE_METRIC;
      const hasOwnerEditPermission = isMetricGoal
        ? allowScorecardMetricPmOwnerEdits
        : allowScorecardOwnerEdits;

      const sendAllFields =
        !withRestrictions || (isActiveOwnScorecard && hasOwnerEditPermission);

      const isNewGoal = !id;
      const sendGoalFields = sendAllFields || isNewGoal;

      const { canEdit: goalCanEdit } = getGoalEditPermissions({
        goalType,
        allowScorecardOwnerEdits,
        allowScorecardMetricPmOwnerEdits,
        isRevOpsManager: metricEditContext.isRevOpsManager,
        isReviewCycleManager: metricEditContext.isReviewCycleManager,
        hasRestrictionForTemplateGoal:
          metricEditContext.hasRestrictionForTemplateGoal,
        isActiveOwnScorecard,
      });
      const sendMetricFields = isMetricGoal && !sendAllFields && goalCanEdit;

      // Only send subgoal weights when owner has edit permission or not on own scorecard
      const sendSubgoalWeightField =
        !isActiveOwnScorecard || hasOwnerEditPermission;

      if (isActiveOwnScorecard) {
        if (hasOwnerEditPermission) {
          Object.assign(goalFields, {
            params: [
              {
                alias: CALCULATE_SELF_ASSESSMENT_PARAMETER_TYPE,
                value: selfAssessmentSetup === SelfAssessmentSetups.calculate,
              },
              {
                alias: ALLOW_SCORECARD_COMMENT_PARAMETER_TYPE,
                value: allowScorecardComment,
              },
              {
                alias: ENABLE_SELF_ASSESSMENT_PARAMETER_TYPE,
                value:
                  selfAssessmentSetup === SelfAssessmentSetups.request ||
                  selfAssessmentSetup === SelfAssessmentSetups.calculate,
              },
            ],
          });
        }
      } else if (sendGoalFields) {
        Object.assign(goalFields, {
          params: [
            {
              alias: ALLOW_SCORECARD_OWNER_EDITS_PARAMETER_TYPE,
              value: allowScorecardOwnerEdits,
            },
            {
              alias: CALCULATE_SELF_ASSESSMENT_PARAMETER_TYPE,
              value: selfAssessmentSetup === SelfAssessmentSetups.calculate,
            },
            {
              alias: ALLOW_SCORECARD_COMMENT_PARAMETER_TYPE,
              value: allowScorecardComment,
            },
            {
              alias: ENABLE_SELF_ASSESSMENT_PARAMETER_TYPE,
              value:
                selfAssessmentSetup === SelfAssessmentSetups.request ||
                selfAssessmentSetup === SelfAssessmentSetups.calculate,
            },
            {
              alias: ALLOW_SCORECARD_METRIC_PM_OWNER_EDITS_PARAMETER_TYPE,
              value: allowScorecardMetricPmOwnerEdits,
            },
            {
              alias: ALLOW_SCORECARD_OWNER_EDIT_ACTUAL_FIELD_PARAMETER_TYPE,
              value: allowScorecardOwnerEditActualField,
            },
            {
              alias: AUTO_ASSIGN_PM_REVIEWER_PARAMETER_TYPE,
              value: autoAssignPmReviewer,
            },
            {
              alias: LIMIT_REVIEWERS_TO_EMPLOYEE_AND_PM_PARAMETER_TYPE,
              value: limitReviewersToEmployeeAndPm,
            },
          ],
        });
      }

      if (sendGoalFields) {
        Object.assign(goalFields, {
          orderRank: goalIndex + 1,
        });
      }

      const isCuSat = isCuSatGoal(goal);
      if (isCuSat) {
        const { fromGoalTemplateId } = goal;
        if (!id && fromGoalTemplateId) {
          Object.assign(goalFields, { fromGoalTemplateId });
        }
      } else {
        if (sendGoalFields) {
          Object.assign(goalFields, {
            name: name?.trim(),
            description: description?.trim(),
          });
        }
        const children = subgoals.map(
          (
            {
              weight: subgoalWeight = 0,
              id: subId,
              name: subName,
              description: subDesc,
              assessmentScaleGroupId,
              metricTarget,
              metricFormat,
              metricPerformanceFactor,
            },
            subgoalIndex,
          ) => {
            const subgoalFields = {};
            const isNewSubgoal = !subId;
            const sendSubgoalFields = sendAllFields || isNewSubgoal;
            if (sendSubgoalWeightField) {
              Object.assign(subgoalFields, {
                weight: Number.parseInt(subgoalWeight as string, 10),
              });
            }
            if (subId) {
              Object.assign(subgoalFields, { id: subId });
            }
            if (sendSubgoalFields) {
              Object.assign(subgoalFields, {
                orderRank: subgoalIndex,
                name: subName?.trim(),
                description: subDesc?.trim(),
                assessmentScaleGroupId,
                ...(isMetricGoal &&
                  metricTarget != null && {
                    metricTarget: String(metricTarget),
                  }),
                ...(isMetricGoal && metricFormat && { metricFormat }),
                ...(isMetricGoal &&
                  metricPerformanceFactor && { metricPerformanceFactor }),
              });
            } else if (sendMetricFields) {
              Object.assign(subgoalFields, {
                ...(metricTarget != null && {
                  metricTarget: String(metricTarget),
                }),
                ...(metricFormat && { metricFormat }),
                ...(metricPerformanceFactor && { metricPerformanceFactor }),
              });
            }
            return subgoalFields as FormSubGoal;
          },
        );
        Object.assign(goalFields, { subgoals: children });
      }

      return goalFields as FormGoal;
    }),
  };
}
